import React, { useState, useRef } from 'react';
import { Plus, Camera } from 'lucide-react';
import { isVideoExtension } from '@/utils/mediaUtils';

interface VehicleMediaCarouselProps {
  vehicleImages: string[];
  isLoading: boolean;
  uploadProgress?: number; // 0–100 while uploading, undefined when idle
  uploadingFilesCount?: number;
  isVideoPermissionsLoading?: boolean;
  canUploadVideo?: boolean;
  onImageClick: (index: number) => void;
  onTakePhoto: () => void;
  uploadButtonText?: string;
  screenName?: string;
  statusBadge?: string;
}

const VehicleMediaCarousel: React.FC<VehicleMediaCarouselProps> = ({
  vehicleImages,
  isLoading,
  uploadProgress,
  uploadingFilesCount = 1,
  isVideoPermissionsLoading = false,
  canUploadVideo = false,
  onImageClick,
  onTakePhoto,
  uploadButtonText,
  screenName = 'Screen',
  statusBadge,
}) => {
  const isUploading = uploadProgress !== undefined;
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [videoThumbnails, setVideoThumbnails] = useState<Record<string, string>>({});
  const [videoAspectRatios, setVideoAspectRatios] = useState<Record<string, number>>({});

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const scrollLeft = scrollContainerRef.current.scrollLeft;
    const itemWidth = scrollContainerRef.current.offsetWidth - 32;
    const newIndex = Math.round(scrollLeft / itemWidth);
    if (newIndex !== currentPhotoIndex) {
      setCurrentPhotoIndex(newIndex);
    }
  };



  // Extract first frame from video and create a thumbnail
  const extractVideoThumbnail = (videoUrl: string): Promise<{ thumbnail: string; aspectRatio: number }> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.src = videoUrl;
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;

      const handleLoadedMetadata = () => {
        try {
          // Seek to first frame (100ms in to avoid any black frame issues)
          video.currentTime = 0.1;
        } catch (error) {
          console.error(`📱 [${screenName}] Error seeking video:`, error);
          video.removeEventListener('seeked', handleSeeked);
          video.removeEventListener('loadedmetadata', handleLoadedMetadata);
          video.removeEventListener('error', handleError);
          reject(error);
        }
      };

      const handleSeeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            throw new Error('Could not get canvas context');
          }

          ctx.drawImage(video, 0, 0);
          const thumbnail = canvas.toDataURL('image/jpeg', 0.8);

          // Clean up
          video.removeEventListener('seeked', handleSeeked);
          video.removeEventListener('loadedmetadata', handleLoadedMetadata);
          video.removeEventListener('error', handleError);

          resolve({ thumbnail, aspectRatio: video.videoWidth / video.videoHeight });
        } catch (error) {
          console.error(`📱 [${screenName}] Error creating thumbnail:`, error);
          video.removeEventListener('seeked', handleSeeked);
          video.removeEventListener('loadedmetadata', handleLoadedMetadata);
          video.removeEventListener('error', handleError);
          reject(error);
        }
      };

      const handleError = (e: Event) => {
        console.error(`📱 [${screenName}] Error loading video for thumbnail:`, e);
        video.removeEventListener('seeked', handleSeeked);
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('error', handleError);
        reject(new Error('Failed to load video'));
      };

      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('seeked', handleSeeked);
      video.addEventListener('error', handleError);
    });
  };

  // Generate thumbnails for all videos when component mounts or images change
  React.useEffect(() => {
    const videosNeedingThumbnails = vehicleImages.filter(
      media => isVideoExtension(media) && !videoThumbnails[media]
    );

    if (videosNeedingThumbnails.length === 0) return;

    const generateThumbnails = async () => {
      const newThumbnails: Record<string, string> = {};
      const newAspectRatios: Record<string, number> = {};

      for (const media of videosNeedingThumbnails) {
        try {
          const { thumbnail, aspectRatio } = await extractVideoThumbnail(media);
          newThumbnails[media] = thumbnail;
          newAspectRatios[media] = aspectRatio;
        } catch (error) {
          console.error(`📱 [${screenName}] Failed to extract thumbnail for ${media}:`, error);
        }
      }

      if (Object.keys(newThumbnails).length > 0) {
        setVideoThumbnails(prev => ({ ...prev, ...newThumbnails }));
        setVideoAspectRatios(prev => ({ ...prev, ...newAspectRatios }));
      }
    };

    generateThumbnails();
  }, [vehicleImages]);

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="relative mb-4">
        <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden relative animate-pulse">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" />
          {vehicleImages.length > 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-gray-400">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-2"></div>
                <p className="text-sm">Loading photos...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // No images state
  if (vehicleImages.length === 0) {
    return (
      <div className="relative mb-4 space-y-2">
        <div
          onClick={isUploading ? undefined : onTakePhoto}
          className={`w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden ${
            isUploading || isVideoPermissionsLoading ? 'opacity-50 cursor-default' : 'cursor-pointer'
          }`}
        >
          <div className="text-center text-gray-500 w-full px-6">
            {isVideoPermissionsLoading ? (
              <>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500 mx-auto mb-2"></div>
                <p className="text-sm">Loading permissions...</p>
              </>
            ) : (
              <>
                <Camera size={32} className="mx-auto mb-2" />
                <p className="text-sm">
                  {canUploadVideo ? 'Add Vehicle Media (Photos & Videos)' : 'Add Vehicle Media'}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Upload Progress Strip */}
        {isUploading && (
          <div className="mt-2 px-0.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-black">Uploading {uploadingFilesCount} {uploadingFilesCount === 1 ? 'file' : 'files'}...</span>
              <span className="text-xs font-medium text-black">{uploadProgress}%</span>
            </div>
            <div className="bg-primary/20 relative h-1.5 w-full overflow-hidden rounded-full">
              <div
                className="bg-primary h-full w-full flex-1 transition-all"
                style={{ transform: `translateX(-${100 - (uploadProgress ?? 0)}%)` }}
              />
            </div>
          </div>
        )}

        <button
          onClick={onTakePhoto}
          disabled={isUploading || isVideoPermissionsLoading}
          className="w-full flex items-center justify-center gap-2 bg-black hover:bg-gray-800 transition-colors py-3 px-4 rounded-lg text-white font-medium disabled:opacity-50"
        >
          <Plus size={16} className="text-white" />
          <span>{uploadButtonText || (canUploadVideo ? 'Add Media' : 'Add Photo')}</span>
        </button>
      </div>
    );
  }

  // Carousel with images
  return (
    <div className="relative mb-4">
      <div className="relative space-y-4">
        {/* Featured Image */}
        <div
          onClick={() => onImageClick(0)}
          className="relative rounded-xl overflow-hidden bg-gray-100 h-60 cursor-pointer hover:opacity-90 transition-opacity flex items-center justify-center"
        >
          {/* Status Badge Overlay */}
          {statusBadge && (
            <div className="absolute top-4 right-4 z-10">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                statusBadge === 'ACTIVE'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {statusBadge}
              </span>
            </div>
          )}
          {(() => {
            const featuredMedia = vehicleImages[0];
            const isVideo = isVideoExtension(featuredMedia);
            const thumbnail = isVideo ? videoThumbnails[featuredMedia] : null;

            if (isVideo && !thumbnail) {
              return (
                <div className="absolute inset-0 bg-gray-200 animate-pulse" />
              );
            }
            return (
              <img
                key={`featured-img-0`}
                src={isVideo ? thumbnail! : featuredMedia}
                alt="Featured vehicle media"
                className="w-full h-full object-cover pointer-events-none"
                onError={(e) => console.error(`📱 [${screenName}] Featured image error:`, e)}
              />
            );
          })()}
        </div>

        {/* Thumbnail Carousel - Only show if more than 1 image */}
        {vehicleImages.length > 1 && (
          <div className="relative">
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="flex gap-2 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-2"
              style={{
                scrollBehavior: 'smooth',
                WebkitOverflowScrolling: 'touch',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
              {/* Show all images except the first one */}
              {vehicleImages.slice(1).map((media, index) => {
                const thumbnailWidth = `calc((100% - 16px) / 3)`;
                const thumbnailHeight = `calc(${thumbnailWidth} * 0.55)`;

                return (
                  <button
                    key={`thumb-${index + 1}`}
                    onClick={() => onImageClick(index + 1)}
                    className={`flex-shrink-0 rounded-lg overflow-hidden snap-start transition-all cursor-pointer hover:opacity-100 ${
                      currentPhotoIndex === index + 1
                        ? 'ring-2 ring-primary'
                        : 'opacity-70'
                    }`}
                    style={{
                      width: thumbnailWidth,
                      height: thumbnailHeight,
                      aspectRatio: '1.818 / 1'
                    }}
                  >
                    {isVideoExtension(media) ? (
                      videoThumbnails[media] ? (
                        <img
                          src={videoThumbnails[media]}
                          alt={`Thumbnail ${index + 2}`}
                          className="w-full h-full object-cover pointer-events-none"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 animate-pulse" />
                      )
                    ) : (
                      <img
                        src={media}
                        alt={`Thumbnail ${index + 2}`}
                        className="w-full h-full object-cover pointer-events-none"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Upload Progress Strip */}
        {isUploading && (
          <div className="mt-2 px-0.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-black">Uploading {uploadingFilesCount} {uploadingFilesCount === 1 ? 'file' : 'files'}...</span>
              <span className="text-xs font-medium text-black">{uploadProgress}%</span>
            </div>
            <div className="bg-primary/20 relative h-1.5 w-full overflow-hidden rounded-full">
              <div
                className="bg-primary h-full w-full flex-1 transition-all"
                style={{ transform: `translateX(-${100 - (uploadProgress ?? 0)}%)` }}
              />
            </div>
          </div>
        )}

        {/* Add Media Button */}
        <button
          onClick={onTakePhoto}
          disabled={isUploading || isVideoPermissionsLoading}
          className="w-full flex items-center justify-center gap-2 bg-black hover:bg-gray-800 transition-colors py-3 px-4 rounded-lg text-white font-medium disabled:opacity-50"
        >
          {isVideoPermissionsLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Loading permissions...</span>
            </>
          ) : (
            <>
              <Plus size={16} className="text-white" />
              <span>
                {uploadButtonText ||
                  (canUploadVideo ? 'Add Media' : 'Add Photo')}
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default VehicleMediaCarousel;

